// /communications/reorder-links — Admin view for re-order link generation and
// analytics (G4.3). MANAGER+ only. Lists recent REORDER_LINK communication
// logs and exposes a "Generate new" CTA that opens the QR share modal.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  KpiCard,
  GLASS,
  cn,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useCommLogs, useCommLogsSummary } from '@/features/communications';
import { StatusBadge, ChannelPill } from '@/features/communications';
import type { CommunicationLog } from '@/features/communications';
import { ReorderShareModal } from './ReorderShareModal';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};

function maskRecipient(recipient: string): string {
  if (recipient === 'BROADCAST') return 'Broadcast';
  if (recipient.includes('@')) {
    const [local, domain] = recipient.split('@');
    return `${local[0]}***@${domain}`;
  }
  if (recipient.startsWith('+') && recipient.length > 6) {
    return `${recipient.slice(0, 4)}****${recipient.slice(-4)}`;
  }
  return recipient;
}

const columns: ColumnDef<CommunicationLog>[] = [
  {
    key: 'channel',
    header: 'Channel',
    render: (_v, row) => <ChannelPill channel={row.channel} />,
  },
  {
    key: 'recipient',
    header: 'Recipient',
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-200 font-mono tabular-nums">
        {maskRecipient(row.recipient)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => <StatusBadge status={row.status} />,
  },
  {
    key: 'reference_id',
    header: 'Bill',
    render: (_v, row) => (
      <span className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
        {row.reference_id ? row.reference_id.slice(0, 8) : '—'}
      </span>
    ),
  },
  {
    key: 'created_at',
    header: 'Sent',
    render: (_v, row) => (
      <span className="text-xs text-neutral-500 dark:text-neutral-400">
        {new Date(row.created_at).toLocaleString('en-IN')}
      </span>
    ),
  },
];

function ReorderLinksContent() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const isManagerPlus = roleLevel >= ROLE_LEVELS.MANAGER;

  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);

  // Query the server filtered by type so the table reconciles with the KPI
  // count — never fetch all log types and slice REORDER_LINK out client-side.
  const { data: logsData, isLoading, isFetching, refetch } = useCommLogs({
    page,
    type: 'REORDER_LINK',
  });
  // Scope the KPI cards to re-order links too — the tenant-wide summary
  // counts every message type (invoices, POs, campaigns …).
  const { data: summary } = useCommLogsSummary('REORDER_LINK');

  const reorderLogs = logsData?.data ?? [];

  if (!isManagerPlus) {
    return (
      <PageContainer>
        <EmptyState
          icon={<svg className="h-12 w-12 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
          title="Manager access required"
          description="Re-order link management is restricted to managers and above."
          action={{ label: 'Back to Communications', onClick: () => navigate('/communications') }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="animate-fade-in">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-pink-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(139,92,246,0.7)]">
          <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
                Re-order Links (G4.3)
              </span>
              <h1 className="mt-3 text-2xl font-bold sm:text-3xl text-white">Scan. Re-order. Return.</h1>
              <p className="mt-1 max-w-xl text-sm text-white/85">
                Send customers a QR-based re-order link after their purchase — they scan,
                adjust quantities, and a DRAFT bill lands at the original location.
              </p>
            </div>
            <Button variant="secondary" onClick={() => setShowModal(true)} className="shrink-0">
              + Generate link
            </Button>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <KpiCard
            title="Re-order clicks"
            value={String(summary?.reorder_clicks ?? 0)}
          />
          <KpiCard
            title="Total sent"
            value={String(summary?.total_sent ?? 0)}
          />
          <KpiCard
            title="Delivery rate"
            value={`${(summary?.delivery_rate ?? 0).toFixed(1)}%`}
          />
        </div>

        {/* Recent links */}
        <div className={cn(GLASS, 'p-5')}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
              Recent re-order links
            </h2>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : reorderLogs.length === 0 ? (
            <EmptyState
              title="No re-order links yet"
              description="Generate your first re-order link to get started."
              action={{ label: 'Generate link', onClick: () => setShowModal(true) }}
            />
          ) : (
            <DataTable
              columns={columns}
              data={reorderLogs}
              loading={isFetching}
              totalCount={logsData?.meta.total}
              currentPage={page}
              pageSize={50}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>

      {showModal && (
        <ReorderShareModal
          onClose={() => setShowModal(false)}
          onCreated={() => { void refetch(); }}
        />
      )}
    </PageContainer>
  );
}

export function ReorderLinksPage() {
  return (
    <ErrorBoundary>
      <ReorderLinksContent />
    </ErrorBoundary>
  );
}
